import { IS_LOCAL_DEV } from 'src/constants/envs';
import { parseAlertSnapshotOverride } from './AlertSnapshotOverride.functions';
import type { tAlertSnapshotOverride } from './AlertCenter.types';

/**
 * Single storage key for the simulator override. Versioned in the name AND
 * in the payload's `version` field — a dev-only tool needs no migration
 * path, only a version check that clears anything that doesn't match.
 */
export const ALERT_SNAPSHOT_OVERRIDE_STORAGE_KEY = 'alertCenter:simulator:v1';

/**
 * In-memory subscribers for same-tab fan-out, mirroring
 * `useLeadAgentDesignOverride`'s `sameTabSubscribers`: the native `storage`
 * event only fires cross-tab, so without this set a sibling consumer
 * mounted in the same tab would never see a preset change.
 */
const sameTabSubscribers = new Set<() => void>();

/**
 * Reads the current override. Every entry point below gates on
 * `IS_LOCAL_DEV` FIRST — this module is imported by `AlertCenter.hook.ts`,
 * which ships to production, so a stale populated key must never leak a
 * simulated snapshot into a deployed build.
 */
export function readAlertSnapshotOverride(): tAlertSnapshotOverride | null {
  if (!IS_LOCAL_DEV) return null;
  if (typeof window === 'undefined') return null;
  return parseAlertSnapshotOverride(
    window.localStorage.getItem(ALERT_SNAPSHOT_OVERRIDE_STORAGE_KEY),
  );
}

/**
 * Persists (or, given `null`, clears) the override and fans out to same-tab
 * subscribers. A no-op outside `IS_LOCAL_DEV`, so `DevToolbar` cannot even
 * write a key in a build where it isn't rendered.
 */
export function writeAlertSnapshotOverride(
  override: tAlertSnapshotOverride | null,
): void {
  if (!IS_LOCAL_DEV) return;
  if (typeof window === 'undefined') return;

  if (override === null) {
    window.localStorage.removeItem(ALERT_SNAPSHOT_OVERRIDE_STORAGE_KEY);
  } else {
    window.localStorage.setItem(
      ALERT_SNAPSHOT_OVERRIDE_STORAGE_KEY,
      JSON.stringify(override),
    );
  }
  sameTabSubscribers.forEach((subscriber) => subscriber());
}

/**
 * Registers a same-tab subscriber; returns the unsubscribe function. A no-op
 * subscription outside `IS_LOCAL_DEV` (the returned unsubscribe is still
 * safe to call).
 */
export function subscribeAlertSnapshotOverride(
  subscriber: () => void,
): () => void {
  if (!IS_LOCAL_DEV) return () => {};
  sameTabSubscribers.add(subscriber);
  return () => {
    sameTabSubscribers.delete(subscriber);
  };
}
